<template>
  <transition name="ph-modal-fade">
    <div
      v-if="open"
      ref="backdrop"
      class="ph-modal-backdrop"
      @click.self="onClose"
      @keydown.esc.stop="onClose"
    >
      <div
        class="ph-modal phd-variant-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="phd-variant-title"
        tabindex="-1"
        ref="modal"
      >
        <button
          type="button"
          class="ph-modal-close"
          @click="onClose"
          aria-label="Close"
        >×</button>

        <header class="ph-modal-header">
          <span class="ph-modal-eyebrow">Pick your version</span>
          <h2 id="phd-variant-title">{{ group ? group.name : '' }}</h2>
          <p>
            We have {{ group ? group.variantCount : 0 }} versions of this course.
            Pick the one that fits your state or role — you can pick more than one.
          </p>
        </header>

        <ul v-if="group && group.variants" class="phd-variant-list">
          <li
            v-for="v in group.variants"
            :key="v.id"
            class="phd-variant-row"
            :class="{ 'phd-variant-row--on': isInCart(v.id) }"
          >
            <div class="phd-variant-body">
              <span class="phd-variant-label">{{ variantLabel(v) }}</span>
              <span v-if="v.length" class="phd-variant-meta">⏱ {{ v.length }}</span>
            </div>
            <span class="phd-variant-price">${{ formatPrice(v.cost) }}</span>
            <button
              type="button"
              class="ph-btn phd-variant-add"
              :class="isInCart(v.id) ? 'ph-btn--in-cart' : 'ph-btn--add'"
              :disabled="busyId === v.id"
              @click="onPick(v.id)"
            >
              <span v-if="busyId === v.id">…</span>
              <span v-else-if="isInCart(v.id)">✓ In cart</span>
              <span v-else>+ Add</span>
            </button>
          </li>
        </ul>

        <button
          type="button"
          class="ph-btn ph-btn--ghost phd-variant-done"
          @click="onClose"
        >Done</button>
      </div>
    </div>
  </transition>
</template>

<script>
import teleportToBody from '../../mixins/teleportToBody';
import { variantLabel as variantLabelUtil } from '../../utils/courseGrouping';

export default {
  name: 'VariantPickerModal',
  mixins: [teleportToBody],

  props: {
    open: { type: Boolean, default: false },
    // Group object as produced by groupCourses() in PricingPage.vue.
    // Shape: { isGroup: true, name, variants: [...], variantCount, ... }
    group: { type: Object, default: null },
    // Set of course IDs currently in the cart (parent passes the cart's
    // items via a prop so we don't have to wire to a store).
    cartIds: { type: Array, default: () => [] },
    // The id parent is currently busy adding/removing — disables one
    // button at a time during the round-trip.
    busyId: { type: [Number, String], default: null },
  },

  watch: {
    open(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.modal && typeof this.$refs.modal.focus === 'function') {
            this.$refs.modal.focus();
          }
        });
      }
    },
  },

  methods: {
    formatPrice(n) {
      const num = Number(n);
      if (!Number.isFinite(num)) return '0.00';
      return num.toFixed(2);
    },
    variantLabel(course) { return variantLabelUtil(course); },
    isInCart(id) {
      return Array.isArray(this.cartIds) && this.cartIds.indexOf(id) !== -1;
    },
    onPick(id) {
      this.$emit('pick', id);
    },
    onClose() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.phd-variant-modal {
  max-width: 540px;
}

.phd-variant-list {
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.phd-variant-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 14px;
  padding: 10px 12px;
  border: 1px solid rgba(37, 37, 37, 0.1);
  border-radius: 10px;
  background: #fff;
  transition: border-color 160ms ease, background-color 160ms ease;
}
.phd-variant-row--on {
  border-color: #fe672c;
  background: #fff8f4;
}

.phd-variant-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.phd-variant-label {
  font-size: 14px;
  line-height: 18px;
  font-weight: 700;
  color: #1a1a1a;
}
.phd-variant-meta {
  font-size: 12px;
  color: #777;
  margin-top: 2px;
}

.phd-variant-price {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  white-space: nowrap;
}

.phd-variant-add {
  font-size: 13px;
  padding: 8px 14px;
  white-space: nowrap;
}

.phd-variant-done {
  width: 100%;
}

@media (max-width: 520px) {
  .phd-variant-row {
    grid-template-columns: 1fr;
    gap: 6px;
  }
  .phd-variant-price { text-align: right; }
}
</style>
